import { InfiniteLoadTrigger, useLayout, useTranslation } from '@ukpostbox/frontend-framework';
import React, { useEffect } from 'react';
import { MailboxLoader } from '../Loaders/MailboxLoader';
import { EndpointError } from '../../../_Global/Components/EndpointError';
import { MailItemToggle } from '../SharedFunctions/MailItem';
import { MailItemDrag, MailItemDragEnd } from '../SharedFunctions/DragAndDrop';
import SelectEverythingBanner from '../../../_Global/Components/SelectEverythingBanner';
import { NoMailEmptyState } from '../EmptyStates/NoMailEmptyState';
import { NoMailSearchResultsEmptyState } from '../EmptyStates/NoMailSearchResultsEmptyState';
import { MailboxEntriesType } from '../Types/MailboxTypes';
import MailboxMailItemMapper from './Entries/MailboxMailItemMapper';
import { MapCustomFolderEntries } from '../FolderEntries/MapCustomFolderEntries';

function MailboxEntries({
	status,
	data,
	error,
	isFetchingNextPage,
	fetchNextPage,
	hasNextPage,
	isStale,
	isRefetching,
	dataUpdatedAt,
	mailBoxInfo,
	gridView,
	customFolderData,
	gridViewRef,
	selectedItems,
	shiftKeyDownRef,
	selectedItemsRef,
	searchActive,
	allSelected,
	visibleSelected,
	draggingActiveCallback,
	toggleSelectedCallback,
	toggleAllCallback,
	moveSelectedItemsCallback,
	handleFolderMoveCallback,
	itemCounts,
}: MailboxEntriesType) {
	const { t } = useTranslation();
	const { isTouchInput, isTiny, isSmall, isMedium } = useLayout();

	useEffect(() => {
		switch (status) {
			case 'pending':
				document.title = t('messageCentre.search.loadingTitle');
				break;
			default:
				document.title = t('messageCentre.search.results.resultsTitle', { count: itemCounts.total });
		}
	}, [itemCounts, status]);

	return (
		<>
			{itemCounts.loaded !== itemCounts.total && selectedItems.length !== itemCounts.total && visibleSelected && (
				<SelectEverythingBanner
					selectedCount={allSelected ? itemCounts.total : selectedItems.length}
					selectedLabel={
						allSelected
							? t('actions.selectLoaded')
							: t('actions.selectAllIn', { location: mailBoxInfo.folderName })
					}
					callback={() => toggleAllCallback(!allSelected)}
				/>
			)}

			{status === 'pending' ? (
				<MailboxLoader />
			) : status === 'error' && error ? (
				<EndpointError {...error} stacked extraClass='m-md' />
			) : (
				<>
					<MapCustomFolderEntries
						customFolderData={customFolderData}
						mailBoxInfo={mailBoxInfo}
						gridView={gridView}
						handleFolderMoveCallback={(result) => handleFolderMoveCallback(result)}
					/>

					{searchActive && !data && <NoMailSearchResultsEmptyState mailBoxInfo={mailBoxInfo} />}

					{data?.pages.map((group, groupIndex) => {
						if (group.totalRecords === 0) {
							return <NoMailEmptyState mailBoxInfo={mailBoxInfo} keySlug={groupIndex} />;
						}

						return group.data.map((mailItem, index) => {
							if (
								mailBoxInfo.customFolderId &&
								mailItem.clientFolder?.parentFolder?.id === mailBoxInfo.customFolderId
							)
								return null;

							return (
								<MailboxMailItemMapper
									mailView={gridView ? 'grid' : 'list'}
									key={`mailboxItem-${mailItem.mailUuid}`}
									index={`${groupIndex}_${index}`}
									selected={allSelected || selectedItems.includes(mailItem.mailUuid)}
									selectable
									callBack={(mailUuid: string) =>
										MailItemToggle({
											selectedMailUuid: mailUuid,
											gridViewRef,
											selectedItemsRef,
											shiftKeyDownRef,
											toggleSelectedCallback: (toggleMailUuid) =>
												toggleSelectedCallback(toggleMailUuid, itemCounts.total),
										})
									}
									draggable={
										!isTouchInput &&
										!isTiny &&
										!isSmall &&
										!isMedium &&
										customFolderData &&
										customFolderData?.length > 0
									}
									onDragStart={(e: DragEvent) =>
										MailItemDrag({
											event: e,
											selectedItems,
											mailboxInfo: mailBoxInfo,
											toggleSelectedCallback: (targetKey) =>
												toggleSelectedCallback(targetKey, itemCounts.total),
											draggingActiveCallback: (state) => draggingActiveCallback(state),
										})
									}
									onDragEnd={(e: DragEvent) =>
										MailItemDragEnd({
											event: e,
											draggingActiveCallback: (state: boolean) => draggingActiveCallback(state),
											moveSelectedItemsCallback: (targetId: number) =>
												moveSelectedItemsCallback(targetId),
										})
									}
									onDragOver={(e: DragEvent) => e.preventDefault()}
									shiftKey={shiftKeyDownRef}
									{...mailItem}
								/>
							);
						});
					})}

					{data && itemCounts.total > 0 && (
						<InfiniteLoadTrigger
							loaded={itemCounts.loaded}
							totalCount={itemCounts.total}
							hasNextPage={hasNextPage}
							fetchNextPage={fetchNextPage}
							isStale={isStale}
							isRefetching={isRefetching}
							isFetchingNextPage={isFetchingNextPage}
						/>
					)}
				</>
			)}
		</>
	);
}

export { MailboxEntries };
